import React from 'react';

const clamp = (v, max) => Math.max(0, Math.min(100, (v / max) * 100));

/**
 * Progress — linear progress bar.
 *
 * Props:
 *   value: number, max: number (default 100)
 *   tone: 'primary'|'success'|'warning'|'danger'  — default 'primary'
 *   size: 'sm' | 'md'
 *   indeterminate: boolean
 *   label: ReactNode | true     — true shows the percentage
 */
const Progress = ({
  value = 0,
  max = 100,
  tone = 'primary',
  size = 'md',
  indeterminate = false,
  label,
  className = '',
}) => {
  const pct = clamp(value, max);
  const showLabel = label === true ? `${Math.round(pct)}%` : label;
  return (
    <div className={`ps-progress ps-progress--${size} ${className}`.trim()}>
      {showLabel != null && <div className="ps-progress__label">{showLabel}</div>}
      <div
        className={`ps-progress__track ps-progress__track--${tone}`}
        role="progressbar"
        aria-valuenow={indeterminate ? undefined : Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={`ps-progress__bar${indeterminate ? ' is-indeterminate' : ''}`}
          style={indeterminate ? undefined : { width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

/**
 * CircularProgress — ring progress, good for compact stats (occupancy etc.).
 *
 * Props:
 *   value, max, size (px), thickness (px), tone, label
 */
export const CircularProgress = ({
  value = 0,
  max = 100,
  size = 56,
  thickness = 6,
  tone = 'primary',
  label,
  className = '',
}) => {
  const pct = clamp(value, max);
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  const showLabel = label === true ? `${Math.round(pct)}%` : label;
  return (
    <div className={`ps-circular ps-circular--${tone} ${className}`.trim()} style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle className="ps-circular__track" cx={size / 2} cy={size / 2} r={r} strokeWidth={thickness} fill="none" />
        <circle
          className="ps-circular__bar"
          cx={size / 2}
          cy={size / 2}
          r={r}
          strokeWidth={thickness}
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      {showLabel != null && <span className="ps-circular__label">{showLabel}</span>}
    </div>
  );
};

export default Progress;
